import React, { useState, useEffect, useRef } from 'react';
import { ReactComponent as OskeyLogo } from './assets/logo_gradient.svg';
import { ReactComponent as BluetoothIcon } from './assets/bluetooth.svg';
import { ReactComponent as PinIcon } from './assets/pincode.svg';
import { ReactComponent as SesameIcon } from './assets/keyfoab.svg';
import { ReactComponent as VisiophonieIcon } from './assets/phone_in_talk.svg';
import { ReactComponent as FacescanIcon } from './assets/face_scan.svg';
import intercomImg from './assets/intercom.png';
import digicomImg from './assets/digicom.png';
import intercomInox from './assets/intercom-inox.png';
import intercomNoir from './assets/intercom-noir.png';
import intercomLaiton from './assets/intercom-laiton.png';
import digicomInox from './assets/digicom-inox.png';
import digicomNoir from './assets/digicom-noir.png';
import digicomLaiton from './assets/digicom-laiton.png';

// Definition of the graphic charter colors
const COLORS = {
  primaryBlue: '#5270FF',
  primaryViolet: '#A66DFF',
  cyan: '#4A96FF',
  darkText: '#333333',
  lightBackground: '#F8F8F8',
  cardBackground: '#FFFFFF',
  borderColor: '#E0E0E0',
  success: '#28A745',
  error: '#DC3545',
  gradientFrom: '#5270FF',
  gradientTo: '#A66DFF',
};

// Source of truth for the form.
const parametersConfig = {
  "Contrôle d'accès": {
    description: 'Choisissez les moyens par lesquels les résidents et visiteurs pourront accéder à la propriété.',
    questions: [{ name: 'access_methods', label: "Les différents modes d'accès", type: 'pills', options: ['Bluetooth', 'Code PIN'] }],
  },
  'Paramétrage code PIN': {
    description: "Si l'accès par code PIN est activé, affinez ici son fonctionnement.",
    questions: [{ name: 'pin_renewal_frequency', label: 'Fréquence de renouvellement automatique', type: 'slider', options: ['3 mois', '6 mois', '1 an', '2 an'] }],
  },
  'Gestion du foyer': {
    description: 'Définissez les permissions des résidents pour la gestion de leur propre foyer.',
    questions: [
      {
        name: 'add_household_members',
        label: "Permettre aux résidents d'ajouter des membres à leur foyer",
        options: ['Autorisé', 'Interdit'],
        descriptionOn: 'Les résidents peuvent ajouter des co-résidents à leur foyer.',
        descriptionOff: 'Seul le gestionnaire peut modifier la composition des foyers.',
      },
      {
        name: 'permanent_invitations',
        label: "Permettre aux résidents d'ajouter des invités permanents à leur foyer",
        options: ['Autorisées', 'Interdites'],
        descriptionOn: 'Les résidents peuvent donner un accès récurrent (ex: famille, aide à domicile).',
        descriptionOff: "Les résidents ne peuvent pas ajouter d'invités permanents.",
      },
    ],
  },
  "Partage d'accès": {
    description: "Définissez les permissions des résidents pour l'envoi d'invitations et le partage d'accès temporaires.",
    questions: [
      {
        name: 'quick_code_generation',
        label: 'Permettre aux résidents de générer des codes rapides',
        options: ['Autorisée', 'Interdite'],
        descriptionOn: 'Les résidents peuvent générer un code à usage unique (ex: livreur).',
        descriptionOff: 'La génération de codes rapides est désactivée.',
        contextualQuestion: {
          name: 'quick_code_validity',
          label: 'Durée de validité maximale des codes rapides',
          type: 'radio',
          options: ['24h', '3 jours', '7 jours'],
        },
      },
      {
        name: 'allow_invitations',
        label: "Permettre aux résidents d'envoyer des invitations",
        options: ['Autorisé', 'Interdit'],
        descriptionOn: "Les résidents pourront envoyer des invitations d'accès.",
        descriptionOff: "Les résidents ne pourront pas envoyer d'invitations.",
        contextualQuestion: {
          name: 'invitation_methods',
          label: "Méthodes d'accès autorisées dans l'invitation",
          type: 'pills',
          options: ['Bluetooth', 'Code PIN'],
        },
      },
    ],
  },
  'Intercom+': {
    description: "Personnalisez l'affichage et les fonctionnalités de la platine Intercom+.",
    questions: [
      {
        name: 'intercom_name_modification',
        label: "Permettre la modification des noms sur la platine depuis l'application",
        options: ['Autorisée pour les résidents', 'Refusée pour les résidents'],
        descriptionOn: "Les résidents peuvent modifier le nom affiché sur l'Intercom+.",
        descriptionOff: 'Seul le gestionnaire peut modifier les noms sur la platine.',
      },
      { name: 'intercom_display_config', label: 'Affichage sur la platine', type: 'checkbox', options: ['Afficher le nom de famille', "Afficher le numéro d'appartement", "Afficher l'étage"] },
    ],
  },
};

// --- Internal CSV parsing function ---
function parseCsv(csvText) {
  if (csvText.startsWith('\uFEFF')) {
    csvText = csvText.substring(1);
  }
  const lines = csvText.trim().split(/\r\n|\n/);
  if (lines.length === 0) return { data: [], meta: { fields: [] } };

  const delimiter = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(delimiter).map((h) => h.trim().replace(/^"|"$/g, ''));
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '') continue;
    const values = lines[i].split(delimiter);
    const row = {};
    headers.forEach((header, index) => {
      const value = values[index] ? values[index].trim() : '';
      row[header] = value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;
    });
    data.push(row);
  }
  return { data, meta: { fields: headers } };
}

// --- ICON COMPONENTS ---
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);
const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// --- FORM AND UI COMPONENTS ---

const isToggleQuestion = (question) => {
  if (question.type || question.options.length !== 2) return false;
  const positiveKeywords = ['accepter', 'autorisé', 'autorisée', 'autorisées'];
  const negativeKeywords = ['refuser', 'refusée', 'interdit', 'interdite', 'interdites'];
  const option1 = question.options[0].toLowerCase();
  const option2 = question.options[1].toLowerCase();
  return (positiveKeywords.some((kw) => option1.includes(kw)) && negativeKeywords.some((kw) => option2.includes(kw))) || (negativeKeywords.some((kw) => option1.includes(kw)) && positiveKeywords.some((kw) => option2.includes(kw)));
};

function ToggleSwitch({ label, name, options, value, onChange, hasError, descriptionOn, descriptionOff }) {
  const isChecked = value === options[0];
  const handleChange = () => onChange({ target: { name, value: isChecked ? options[1] : options[0], type: 'radio' } });
  const gradientStyle = isChecked ? { backgroundImage: `linear-gradient(to right, ${COLORS.gradientFrom}, ${COLORS.gradientTo})` } : {};
  return (
    <div className="py-2">
      <div className="flex items-center justify-between">
        <span className={`font-semibold pr-4 text-[${COLORS.darkText}] ${hasError ? 'text-red-600' : ''}`}>{label}</span>
        <label htmlFor={name} className="flex items-center cursor-pointer">
          <div className="relative">
            <input type="checkbox" id={name} className="sr-only" checked={isChecked} onChange={handleChange} />
            <div className="block w-11 h-6 rounded-full transition-colors bg-gray-300" style={gradientStyle}></div>
            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${isChecked ? 'translate-x-5' : ''}`}></div>
          </div>
        </label>
      </div>
      {(descriptionOn || descriptionOff) && <p className={`text-sm text-[${COLORS.darkText}]/80 mt-2 pr-16`}>{isChecked ? descriptionOn : descriptionOff}</p>}
    </div>
  );
}

function Slider({ label, name, options, value, onChange, hasError }) {
  const currentIndex = options.indexOf(value);
  const valueIndex = currentIndex === -1 ? 0 : currentIndex;
  const handleSliderChange = (event) => onChange({ target: { name, value: options[parseInt(event.target.value, 10)], type: 'slider' } });
  return (
    <div className="py-2">
      <label className={`font-semibold text-[${COLORS.darkText}] ${hasError ? 'text-red-600' : ''}`}>{label}</label>
      <div className="mt-3">
        <input type="range" min="0" max={options.length - 1} value={valueIndex} onChange={handleSliderChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-thumb-gradient" />
        <div className={`flex justify-between text-xs text-[${COLORS.darkText}]/80 mt-2`}>
          {options.map((option, index) => (
            <span key={option} className="cursor-pointer" onClick={() => handleSliderChange({ target: { value: index } })}>
              {option}
            </span>
          ))}
        </div>
        <div className={`text-center font-bold text-transparent bg-clip-text bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] mt-2`}>Sélection : {options[valueIndex]}</div>
      </div>
    </div>
  );
}

function PillSelector({ label, name, options, value, onChange, hasError, disabledOptions = [], icons = {} }) {
  const selectedPills = new Set(value || []);
  const handlePillClick = (option) => {
    const isEnabled = !disabledOptions.includes(option);
    if (!isEnabled) return;
    const newSelectedPills = new Set(selectedPills);
    if (newSelectedPills.has(option)) newSelectedPills.delete(option);
    else newSelectedPills.add(option);
    onChange({ target: { name, value: Array.from(newSelectedPills), type: 'pills' } });
  };
  return (
    <div className="py-2">
      <label className={`font-semibold text-[${COLORS.darkText}] ${hasError ? 'text-red-600' : ''}`}>{label}</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selectedPills.has(option);
          const isEnabled = !disabledOptions.includes(option);
          const baseClasses = 'px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 flex items-center justify-center shadow-md';
          const selectedClasses = `text-white bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg hover:scale-105`;
          const unselectedClasses = `bg-white text-[${COLORS.darkText}] hover:bg-gray-100`;
          const disabledClasses = `bg-gray-200 text-gray-400 cursor-not-allowed`;

          const pillClasses = `${baseClasses} ${isEnabled ? (isSelected ? selectedClasses : unselectedClasses) : disabledClasses}`;
          const IconComponent = icons[option];
          const iconColorClass = isSelected ? '' : `text-[${COLORS.primaryBlue}]`;

          return (
            <div key={option} className="relative group">
              <button type="button" className={pillClasses} onClick={() => handlePillClick(option)} disabled={!isEnabled}>
                {IconComponent && <IconComponent className={`h-4 w-4 mr-2 ${iconColorClass}`} />}
                {option}
              </button>
              {!isEnabled && (
                <div className="absolute bottom-full left-1/2 z-10 -translate-x-1/2 mb-2 w-max p-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Désactivé car non autorisé pour la propriété.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConfirmationModal({ isOpen, onClose, onConfirm, summary, isLoading, statusMessage }) {
  if (!isOpen) return null;

  const SuccessIcon = () => (
    <svg className="mx-auto h-12 w-12 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const ErrorIcon = () => (
    <svg className="mx-auto h-12 w-12 text-red-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const LoadingIcon = () => (
    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className={`relative mx-auto p-6 border-0 w-full max-w-lg shadow-2xl rounded-2xl bg-[${COLORS.cardBackground}]`}>
        <div className="mt-3 text-center">
          {statusMessage.text ? (
            // --- Success or Error View ---
            <div>
              {statusMessage.type === 'success' ? <SuccessIcon /> : <ErrorIcon />}
              <h3 className="text-xl leading-6 font-bold text-gray-800 mt-4">{statusMessage.type === 'success' ? 'Configuration Envoyée !' : 'Erreur'}</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-600">{statusMessage.text}</p>
              </div>
              <div className="items-center px-4 py-3 mt-4">
                <button onClick={onClose} className={`w-full px-6 py-3 rounded-xl shadow-md transition-all duration-300 bg-gray-200 text-[${COLORS.darkText}] hover:bg-gray-300 font-semibold`}>
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            // --- Summary View ---
            <div>
              <h3 className={`text-xl leading-6 font-bold text-transparent bg-clip-text bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}]`}>Récapitulatif de votre configuration</h3>
              <div className="mt-4 px-2 py-3 max-h-72 overflow-y-auto text-left">
                <div className="space-y-5">
                  {Object.entries(summary).map(([category, choices]) => {
                    if (Object.keys(choices).length === 0 && category !== 'Prestataires') return null;
                    return (
                      <div key={category}>
                        <h4 className={`font-bold text-lg text-[${COLORS.darkText}]`}>{category}</h4>
                        <ul className={`list-disc list-inside text-sm text-[${COLORS.darkText}]/80 space-y-1 mt-2`}>
                          {Object.entries(choices).map(([label, value]) => (
                            <li key={label}>
                              <span className="font-semibold">{label}:</span> {Array.isArray(value) ? value.join(', ') : value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row-reverse items-center justify-center gap-4 px-4 py-3 mt-4">
                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className={`w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[${COLORS.success}] to-green-600 text-white text-base font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:scale-100 flex items-center justify-center`}
                >
                  {isLoading && <LoadingIcon />}
                  {isLoading ? 'Envoi en cours...' : 'Confirmer et envoyer'}
                </button>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className={`w-full sm:w-auto px-6 py-3 bg-gray-200 text-[${COLORS.darkText}] text-base font-semibold rounded-xl shadow-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-300`}
                >
                  Modifier
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddProviderModal({ isOpen, onClose, onAddProvider }) {
  const [provider, setProvider] = useState({ societe: '', email: '', adresse: '', code_postal: '', commune: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProvider((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    onAddProvider(provider);
    setProvider({ societe: '', email: '', adresse: '', code_postal: '', commune: '' }); // Reset form
    onClose();
  };

  if (!isOpen) return null;
  const isFormValid = provider.societe && provider.email && provider.adresse && provider.code_postal && provider.commune;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className={`relative mx-auto p-6 border-0 w-full max-w-lg shadow-2xl rounded-2xl bg-[${COLORS.cardBackground}]`}>
        <h3 className={`text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] mb-4`}>Ajouter un prestataire</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="societe" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Société
              </label>
              <input
                type="text"
                name="societe"
                id="societe"
                value={provider.societe}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="email" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={provider.email}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="adresse" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
              Adresse
            </label>
            <input
              type="text"
              name="adresse"
              id="adresse"
              value={provider.adresse}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="code_postal" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Code postal
              </label>
              <input
                type="text"
                name="code_postal"
                id="code_postal"
                value={provider.code_postal}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="commune" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Commune
              </label>
              <input
                type="text"
                name="commune"
                id="commune"
                value={provider.commune}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <button onClick={onClose} className={`px-4 py-2 bg-gray-200 text-[${COLORS.darkText}] rounded-xl hover:bg-gray-300 transition-all duration-300`}>
            Annuler
          </button>
          <button
            onClick={handleAdd}
            disabled={!isFormValid}
            className={`px-4 py-2 text-white rounded-xl bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg hover:scale-105 transform transition-all duration-300 disabled:opacity-50 disabled:scale-100`}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ isOpen, onClose, onSaveUser, userToEdit }) {
  const [user, setUser] = useState({ nom_prenom: '', email: '', telephone: '' });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (userToEdit) {
      setUser(userToEdit);
      setIsEditing(true);
    } else {
      setUser({ nom_prenom: '', email: '', telephone: '' });
      setIsEditing(false);
    }
  }, [userToEdit, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSaveUser(user);
    onClose();
  };

  if (!isOpen) return null;

  const isFormValid = user.nom_prenom && user.email && user.telephone;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className={`relative mx-auto p-6 border-0 w-full max-w-lg shadow-2xl rounded-2xl bg-[${COLORS.cardBackground}]`}>
        <h3 className={`text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] mb-4`}>{isEditing ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="nom_prenom" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
              Nom & Prénom
            </label>
            <input
              type="text"
              name="nom_prenom"
              id="nom_prenom"
              value={user.nom_prenom}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
            />
          </div>
          <div>
            <label htmlFor="email" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
              Adresse email professionnelle
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={user.email}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
            />
          </div>
          <div>
            <label htmlFor="telephone" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
              Numéro de téléphone portable
            </label>
            <input
              type="tel"
              name="telephone"
              id="telephone"
              value={user.telephone}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-[${COLORS.primaryBlue}]`}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <button onClick={onClose} className={`px-4 py-2 bg-gray-200 text-[${COLORS.darkText}] rounded-xl hover:bg-gray-300 transition-all duration-300`}>
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!isFormValid}
            className={`px-4 py-2 text-white rounded-xl bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg hover:scale-105 transform transition-all duration-300 disabled:opacity-50 disabled:scale-100`}
          >
            {isEditing ? 'Enregistrer les modifications' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CsvInfoModal({ isOpen, onClose, onFileSelect }) {
  const fileInputRef = useRef(null);
  const expectedHeaders = ['nom', 'prenom', 'email', 'telephone', 'numero_lot', 'etage', 'Nature du lot', 'N° fiscal du lot'];

  const handleFileChange = (event) => {
    onFileSelect(event);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className={`relative mx-auto p-6 border-0 w-full max-w-2xl shadow-2xl rounded-2xl bg-[${COLORS.cardBackground}]`}>
        <h3 className={`text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}]`}>Informations sur le fichier</h3>
        <div className={`mt-4 text-sm text-[${COLORS.darkText}]/80 space-y-4`}>
          <p>Votre fichier (CSV, XLSX) doit contenir les informations nécessaires pour identifier chaque résident. L'ordre des colonnes n'a pas d'importance.</p>
          <p className="font-semibold">Exemple de format recommandé :</p>
          <div className="overflow-x-auto p-2 bg-gray-100 rounded-lg">
            <table className="w-full text-xs text-center">
              <thead className="bg-gray-700 text-white font-mono">
                <tr>
                  {expectedHeaders.map((h) => (
                    <th key={h} className="p-2 border-r border-gray-600 last:border-r-0">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr>
                  <td className="p-2 border-r border-gray-200">Dupont</td>
                  <td className="p-2 border-r border-gray-200">Jean</td>
                  <td className="p-2 border-r border-gray-200">jean.dupont@email.com</td>
                  <td className="p-2 border-r border-gray-200">0612345678</td>
                  <td className="p-2 border-r border-gray-200">A101</td>
                  <td className="p-2 border-r border-gray-200">1</td>
                  <td className="p-2 border-r border-gray-200">Parking</td>
                  <td className="p-2">123456789</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <button onClick={onClose} className={`px-4 py-2 bg-gray-200 text-[${COLORS.darkText}] rounded-xl hover:bg-gray-300 transition-all duration-300`}>
            Annuler
          </button>
          <button onClick={() => fileInputRef.current.click()} className={`px-4 py-2 text-white rounded-xl bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg hover:scale-105 transform transition-all duration-300`}>
            Continuer et choisir un fichier
          </button>
          <input type="file" ref={fileInputRef} accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={handleFileChange} />
        </div>
      </div>
    </div>
  );
}

// --- PAGE COMPONENTS ---
function PropertyPage({ propertyInfo, setPropertyInfo, portalUsers, setPortalUsers }) {
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPropertyInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveUser = (user) => {
    if (user.id) {
      setPortalUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
    } else {
      setPortalUsers((prev) => [...prev, { ...user, id: Date.now() }]);
    }
    setUserToEdit(null);
  };

  const handleEditUser = (user) => {
    setUserToEdit(user);
    setIsUserModalOpen(true);
  };

  const handleDeleteUser = (id) => {
    setPortalUsers((prev) => prev.filter((u) => u.id !== id));
  };

  return (
    <>
      <AddUserModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setUserToEdit(null);
        }}
        onSaveUser={handleSaveUser}
        userToEdit={userToEdit}
      />
      <div className="space-y-8">
        <section className={`bg-[${COLORS.cardBackground}] p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
          <h2 className={`text-2xl font-bold mb-2 text-[${COLORS.darkText}]`}>Informations Générales</h2>
          <p className={`text-sm text-[${COLORS.darkText}]/70 mb-6`}>Veuillez renseigner les informations concernant la propriété.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div className="md:col-span-2 font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-2">Propriété</div>
            <div>
              <label htmlFor="name" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Nom de la propriété
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={propertyInfo.name}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-1 focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="address" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Adresse
              </label>
              <input
                type="text"
                name="address"
                id="address"
                value={propertyInfo.address}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-1 focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="zip" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Code postal
              </label>
              <input
                type="text"
                name="zip"
                id="zip"
                value={propertyInfo.zip}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-1 focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="city" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Commune
              </label>
              <input
                type="text"
                name="city"
                id="city"
                value={propertyInfo.city}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-1 focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="nom_gestionnaire" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Nom du gestionnaire
              </label>
              <input
                type="text"
                name="nom_gestionnaire"
                id="nom_gestionnaire"
                value={propertyInfo.nom_gestionnaire}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-1 focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
            <div>
              <label htmlFor="nom_proprietaire" className={`block text-sm font-medium text-[${COLORS.darkText}]/90`}>
                Nom du propriétaire
              </label>
              <input
                type="text"
                name="nom_proprietaire"
                id="nom_proprietaire"
                value={propertyInfo.nom_proprietaire}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border-[${COLORS.borderColor}] shadow-sm focus:border-[${COLORS.primaryBlue}] focus:ring-1 focus:ring-[${COLORS.primaryBlue}]`}
              />
            </div>
          </div>
        </section>
        <section className={`bg-[${COLORS.cardBackground}] p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
          <h2 className={`text-2xl font-bold mb-2 text-[${COLORS.darkText}]`}>Utilisateurs du Portail de Gestion Oskey</h2>
          <p className={`text-sm text-[${COLORS.darkText}]/70 mb-4`}>Listez ici tous les futurs utilisateurs du portail de gestion et leurs coordonnées pour que nous puissions créer leurs accès.</p>
          <button
            onClick={() => setIsUserModalOpen(true)}
            className={`px-5 py-2.5 text-white font-semibold rounded-xl bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg hover:scale-105 transform transition-all duration-300 shadow-md`}
          >
            Ajouter un utilisateur
          </button>
          {portalUsers.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className={`bg-gray-50`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Nom & Prénom</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Email</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Téléphone</th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {portalUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900`}>{user.nom_prenom}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500`}>{user.email}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500`}>{user.telephone}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                        <button onClick={() => handleEditUser(user)} className="text-blue-600 hover:text-blue-800">
                          Modifier
                        </button>
                        <button onClick={() => handleDeleteUser(user.id)} className={`text-[${COLORS.error}] hover:text-red-700 transition-colors`}>
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function ProductConfigPage({ productConfig, setProductConfig }) {
  const handleConfigChange = (door, field, value) => {
    setProductConfig((prev) => ({
      ...prev,
      [door]: {
        ...prev[door],
        [field]: value,
      },
    }));
  };

  const doors = [
    { id: 'porteRue', name: 'Porte Rue' },
    { id: 'porteHall', name: 'Porte Hall' },
  ];

  const products = ['Intercom+', 'Digicom+'];
  const installations = ['En applique', 'Encastrée'];
  const plates = ['Aucune', 'Noire', 'Inox', 'Laiton'];
  const plateCosts = { Noire: 60, Inox: 60, Laiton: 75, Aucune: 0 };

  const productImages = {
    'Intercom+': {
      Aucune: intercomImg,
      Noire: intercomNoir,
      Inox: intercomInox,
      Laiton: intercomLaiton,
    },
    'Digicom+': {
      Aucune: digicomImg,
      Noire: digicomNoir,
      Inox: digicomInox,
      Laiton: digicomLaiton,
    },
  };

  const productFeatures = {
    'Digicom+': [
      { name: 'Bluetooth', icon: <BluetoothIcon className="h-5 w-5 text-gray-700" /> },
      { name: 'Code PIN', icon: <PinIcon className="h-5 w-5 text-gray-700" /> },
      { name: 'Sesame+', icon: <SesameIcon className="h-5 w-5 text-gray-700" /> },
    ],
    'Intercom+': [
      { name: 'Bluetooth', icon: <BluetoothIcon className="h-5 w-5 text-gray-700" /> },
      { name: 'Code PIN', icon: <PinIcon className="h-5 w-5 text-gray-700" /> },
      { name: 'Sesame+', icon: <SesameIcon className="h-5 w-5 text-gray-700" /> },
      { name: 'Visiophonie', icon: <VisiophonieIcon className="h-5 w-5 text-gray-700" /> },
      { name: 'Reconnaissance faciale (à terme)', icon: <FacescanIcon className="h-5 w-5 text-gray-700" /> },
    ],
  };

  const summaryData = {
    'Intercom+ / Digicom+': { cost: 6, pros: ["Prise d'appels depuis la rue", 'Accès aux boîtes aux lettres facilité'], cons: ['Pas de visiophonie dans le hall'] },
    'Digicom+ / Intercom+': { cost: 6, pros: ['Usages classiques respectés'], cons: ['Travaux à prévoir pour la platine Intercom+ dans le hall'] },
    'Intercom+ / Intercom+': { cost: 7, pros: ['Visiophonie sur chaque accès'], cons: ['Travaux à prévoir pour la platine Intercom+ dans le hall'] },
    'Digicom+ / Digicom+': { cost: 6, pros: ['Solution la plus économique'], cons: ['Aucune visiophonie'] },
  };

  const currentKey = `${productConfig.porteRue.product || 'Produit'} / ${productConfig.porteHall.product || 'Produit'}`;
  const currentSummary = summaryData[currentKey] || { cost: 0, pros: [], cons: [] };

  const oneTimeCost = (plateCosts[productConfig.porteRue.finishingPlate] || 0) + (plateCosts[productConfig.porteHall.finishingPlate] || 0);

  return (
    <div className="space-y-8">
      <section className={`bg-[${COLORS.cardBackground}] p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
        <h2 className={`text-2xl font-bold mb-2 text-[${COLORS.darkText}]`}>Configuration des Accès</h2>
        <p className={`text-sm text-[${COLORS.darkText}]/70 mb-6`}>Configurez les produits pour chaque porte de votre propriété.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {doors.map((door) => (
            <div key={door.id} className="bg-gray-50 p-6 rounded-xl border border-gray-200 space-y-6">
              <h3 className="font-bold text-xl text-gray-800">{door.name}</h3>

              <div className="grid grid-cols-2 gap-4 items-center">
                {/* Features column */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-700 mb-2">Fonctionnalités incluses</h4>
                  <ul className="text-sm space-y-2">
                    {(productFeatures[productConfig[door.id].product] || []).map((feature) => (
                      <li key={feature.name} className="flex items-center text-gray-600">
                        {feature.icon}
                        <span className="ml-2">{feature.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Image column */}
                <div className="w-full h-48 rounded-lg flex items-center justify-center overflow-hidden">
                  {(() => {
                    const selectedProduct = productConfig[door.id]?.product;
                    const selectedPlate = productConfig[door.id]?.finishingPlate;

                    if (selectedProduct) {
                      const imageUrl = productImages[selectedProduct]?.[selectedPlate];
                      return <img src={imageUrl} alt={`${selectedProduct} avec tôle ${selectedPlate}`} className="object-contain w-full h-full" />;
                    } else {
                      return <span className="text-gray-500 text-xs text-center p-2">Sélectionnez un produit pour voir l'aperçu</span>;
                    }
                  })()}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Produit</h4>
                <div className="flex gap-2">
                  {products.map((product) => (
                    <button
                      key={product}
                      onClick={() => handleConfigChange(door.id, 'product', product)}
                      className={`flex-1 p-3 rounded-lg text-sm font-semibold transition-all duration-200 ${productConfig[door.id]?.product === product ? 'text-white bg-gradient-to-r from-blue-500 to-violet-500 shadow-md' : 'bg-white hover:bg-gray-100'}`}
                    >
                      {product}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Type d'installation</h4>
                <div className="flex gap-2">
                  {installations.map((type) => (
                    <button
                      key={type}
                      onClick={() => handleConfigChange(door.id, 'installation', type)}
                      className={`flex-1 p-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                        productConfig[door.id]?.installation === type ? 'text-white bg-gradient-to-r from-blue-500 to-violet-500 shadow-md' : 'bg-white hover:bg-gray-100'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Tôle de finition</h4>
                <select value={productConfig[door.id]?.finishingPlate || ''} onChange={(e) => handleConfigChange(door.id, 'finishingPlate', e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg">
                  {plates.map((plate) => (
                    <option key={plate} value={plate}>
                      {plate}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-700">Conserver Vigik ?</h4>
                <ToggleSwitch name={`keepVigik_${door.id}`} options={['Oui', 'Non']} value={productConfig[door.id]?.keepVigik ? 'Oui' : 'Non'} onChange={() => handleConfigChange(door.id, 'keepVigik', !productConfig[door.id]?.keepVigik)} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={`bg-white p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
        <h2 className={`text-2xl font-bold mb-4 text-[${COLORS.darkText}]`}>Récapitulatif de la Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
          <div className="bg-gray-100 p-4 rounded-xl relative group">
            <h3 className="font-bold text-gray-600">Coût Mensuel</h3>
            <p className="text-3xl font-black mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-violet-500">{currentSummary.cost}€</p>
            <p className="text-xs text-gray-500">
              HT / mois & par lot <InfoIcon />
            </p>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs p-3 text-xs font-normal text-white bg-gray-900 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              L'offre standard (Intercom+/Digicom+) est à 6€/mois. L'Intercom+ coûte 1€ de plus que le Digicom+.
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900"></div>
            </div>
          </div>
          <div className="bg-gray-100 p-4 rounded-xl relative group">
            <h3 className="font-bold text-gray-600">Coût Unique Installation</h3>
            <p className="text-3xl font-black mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-violet-500">{oneTimeCost}€</p>
            <p className="text-xs text-gray-500">
              Paiement unique <InfoIcon />
            </p>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs p-3 text-xs font-normal text-white bg-gray-900 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Coût des tôles de finition. Noire/Inox: 60€, Laiton: 75€.
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900"></div>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-xl">
            <h3 className="font-bold text-green-800">Avantages</h3>
            <ul className="text-sm text-green-700 mt-2 space-y-1 text-left">{currentSummary.pros.length > 0 ? currentSummary.pros.map((pro) => <li key={pro}>✓ {pro}</li>) : <li>-</li>}</ul>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl">
            <h3 className="font-bold text-blue-800">À noter</h3>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 text-left">{currentSummary.cons.length > 0 ? currentSummary.cons.map((con) => <li key={con}>ⓘ {con}</li>) : <li>-</li>}</ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function ResidentsPage({ residentsData, setResidentsData, fileName, setFileName, isFileLoaded, setIsFileLoaded, csvError, setCsvError, providers, setProviders }) {
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
  const [isCsvInfoModalOpen, setIsCsvInfoModalOpen] = useState(false);

  const handleFileChange = async (event) => {
    /* global XLSX */
    const file = event.target.files[0];
    if (!file) {
      setFileName('Aucun fichier sélectionné');
      return;
    }

    setCsvError(null);
    setIsFileLoaded(false);
    setFileName(file.name);

    const fileExtension = file.name.split('.').pop().toLowerCase();

    try {
      let parsedData = [];
      if (fileExtension === 'csv' || fileExtension === 'txt') {
        const text = await file.text();
        const result = parseCsv(text);
        parsedData = result.data;
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        if (typeof window.XLSX === 'undefined') {
          setCsvError("La librairie de lecture Excel (SheetJS) n'est pas chargée.");
          return;
        }
        const data = await file.arrayBuffer();
        const workbook = window.XLSX.read(data);
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        parsedData = window.XLSX.utils.sheet_to_json(worksheet);
      } else {
        setCsvError('Format de fichier non supporté. Veuillez utiliser un fichier CSV ou Excel.');
        return;
      }

      if (!parsedData || parsedData.length === 0) {
        setCsvError('Le fichier est vide ou son format est incorrect.');
        setResidentsData([]);
      } else {
        setResidentsData(parsedData);
        setIsFileLoaded(true);
      }
    } catch (error) {
      console.error('Erreur de lecture du fichier:', error);
      setCsvError('Une erreur est survenue lors de la lecture du fichier.');
      setResidentsData([]);
    }
  };

  const handleAddProvider = (provider) => {
    setProviders((prev) => [...prev, { ...provider, id: Date.now() }]);
  };

  const handleDeleteProvider = (id) => {
    setProviders((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <>
      <CsvInfoModal isOpen={isCsvInfoModalOpen} onClose={() => setIsCsvInfoModalOpen(false)} onFileSelect={handleFileChange} />
      <AddProviderModal isOpen={isProviderModalOpen} onClose={() => setIsProviderModalOpen(false)} onAddProvider={handleAddProvider} />
      <div className="space-y-8">
        <section className={`bg-[${COLORS.cardBackground}] p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
          <div className="flex justify-between items-center mb-2">
            <h2 className={`text-2xl font-bold text-[${COLORS.darkText}]`}>Informations sur les résidents</h2>
          </div>
          <p className={`text-sm text-[${COLORS.darkText}]/70 mb-4`}>Importez la liste des résidents via un fichier CSV ou Excel.</p>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsCsvInfoModalOpen(true)}
              className={`px-5 py-2.5 text-white font-semibold rounded-xl bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg hover:scale-105 transform transition-all duration-300`}
            >
              Choisir un fichier
            </button>
            <span className={`text-[${COLORS.darkText}]/70`}>{fileName}</span>
          </div>
          {csvError && <p className={`text-sm text-[${COLORS.error}] mt-3`}>{csvError}</p>}
          {isFileLoaded && !csvError && (
            <p className="text-sm mt-3 font-semibold" style={{ color: COLORS.success }}>
              {residentsData.length} résident(s) trouvé(s).
            </p>
          )}
        </section>
        <section className={`bg-[${COLORS.cardBackground}] p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
          <h2 className={`text-2xl font-bold mb-2 text-[${COLORS.darkText}]`}>Prestataires</h2>
          <p className={`text-sm text-[${COLORS.darkText}]/70 mb-4`}>Indiquez les prestataires intervenant sur la propriété (ex: ménage, maintenance). Pour chaque prestataire, Oskey générera un code d'accès unique.</p>
          <button onClick={() => setIsProviderModalOpen(true)} className={`px-5 py-2.5 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all duration-300 shadow-md`}>
            Ajouter un prestataire
          </button>
          {providers.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className={`bg-gray-50`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Société</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Email</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Adresse</th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Supprimer</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {providers.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900`}>{p.societe}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500`}>{p.email}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500`}>{`${p.adresse}, ${p.code_postal} ${p.commune}`}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => handleDeleteProvider(p.id)} className={`text-[${COLORS.error}] hover:text-red-700 transition-colors`}>
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function ParametersPage({ selectedParameters, setSelectedParameters, productConfig }) {
  const handleParameterChange = (event) => {
    const { name, value, type, checked } = event.target;
    let finalValue;

    if (type === 'checkbox') {
      const existing = selectedParameters[name] || [];
      finalValue = checked ? [...existing, value] : existing.filter((v) => v !== value);
    } else {
      finalValue = value;
    }

    setSelectedParameters((prev) => ({ ...prev, [name]: finalValue }));
  };

  const sanitizeForId = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, '_');
  const accessIcons = { Bluetooth: BluetoothIcon, 'Code PIN': PinIcon };
  const hasIntercom = productConfig.porteRue.product === 'Intercom+' || productConfig.porteHall.product === 'Intercom+';

  return (
    <div className="space-y-8">
      <section className={`bg-[${COLORS.cardBackground}] p-6 rounded-2xl shadow-lg border border-gray-200/80`}>
        <h2 className={`text-2xl font-bold mb-4 text-[${COLORS.darkText}]`}>Paramétrage de la solution</h2>
        <form className="space-y-8">
          {Object.entries(parametersConfig).map(([category, config]) => {
            if (category === 'Intercom+' && !hasIntercom) return null;
            if (category === 'Paramétrage code PIN' && !(selectedParameters.access_methods || []).includes('Code PIN')) return null;
            return (
              <div key={category} className={`bg-white p-5 rounded-xl border border-gray-200/80`}>
                <h3 className={`text-lg font-bold text-[${COLORS.darkText}]`}>{category}</h3>
                <p className={`text-sm text-[${COLORS.darkText}]/70 mt-1 mb-4`}>{config.description}</p>
                <div className={`space-y-4 pt-4 border-t border-gray-200/80`}>
                  {config.questions.map((question) => (
                    <div key={question.name} className="pt-2 first:pt-0">
                      {isToggleQuestion(question) ? (
                        <ToggleSwitch {...question} value={selectedParameters[question.name]} onChange={handleParameterChange} />
                      ) : question.type === 'slider' ? (
                        <Slider {...question} value={selectedParameters[question.name]} onChange={handleParameterChange} />
                      ) : question.type === 'pills' ? (
                        <PillSelector
                          {...question}
                          value={selectedParameters[question.name]}
                          onChange={handleParameterChange}
                          icons={accessIcons}
                          disabledOptions={question.name === 'invitation_methods' ? parametersConfig["Contrôle d'accès"].questions[0].options.filter((opt) => !(selectedParameters.access_methods || []).includes(opt)) : []}
                        />
                      ) : question.type === 'checkbox' ? (
                        <div className="py-2">
                          <label className={`font-semibold text-[${COLORS.darkText}]`}>{question.label}</label>
                          <div className="mt-2 space-y-2">
                            {question.options.map((option) => (
                              <div key={option} className="flex items-center">
                                <input
                                  id={`${question.name}_${sanitizeForId(option)}`}
                                  name={question.name}
                                  type="checkbox"
                                  value={option}
                                  onChange={handleParameterChange}
                                  checked={(selectedParameters[question.name] || []).includes(option)}
                                  className={`h-4 w-4 rounded border-gray-300 text-[${COLORS.primaryBlue}] focus:ring-2 focus:ring-[${COLORS.primaryBlue}]/50`}
                                />
                                <label htmlFor={`${question.name}_${sanitizeForId(option)}`} className={`ml-3 text-sm text-[${COLORS.darkText}]/80`}>
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="py-2">
                          <label className={`font-semibold text-[${COLORS.darkText}]`}>{question.label}</label>
                          <div className="mt-2 space-y-2">
                            {question.options.map((option) => (
                              <div key={option} className="flex items-center">
                                <input
                                  id={`${question.name}_${sanitizeForId(option)}`}
                                  name={question.name}
                                  type="radio"
                                  value={option}
                                  onChange={handleParameterChange}
                                  checked={selectedParameters[question.name] === option}
                                  className={`h-4 w-4 text-[${COLORS.primaryBlue}] border-gray-300 focus:ring-2 focus:ring-[${COLORS.primaryBlue}]/50`}
                                />
                                <label htmlFor={`${question.name}_${sanitizeForId(option)}`} className={`ml-3 text-sm text-[${COLORS.darkText}]/80`}>
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {question.contextualQuestion && selectedParameters[question.name] === question.options[0] && (
                        <div className={`py-2 pl-4 mt-2 border-l-4 border-[${COLORS.primaryBlue}]/30`}>
                          {question.contextualQuestion.type === 'pills' ? (
                            <PillSelector
                              {...question.contextualQuestion}
                              value={selectedParameters[question.contextualQuestion.name]}
                              onChange={handleParameterChange}
                              icons={accessIcons}
                              disabledOptions={parametersConfig["Contrôle d'accès"].questions.find((q) => q.name === 'access_methods').options.filter((opt) => !(selectedParameters.access_methods || []).includes(opt))}
                            />
                          ) : (
                            <div>
                              <label className={`font-semibold text-sm text-[${COLORS.darkText}]`}>{question.contextualQuestion.label}</label>
                              <div className="mt-2 space-y-2">
                                {question.contextualQuestion.options.map((opt) => (
                                  <div key={opt} className="flex items-center">
                                    <input
                                      id={`${question.contextualQuestion.name}_${sanitizeForId(opt)}`}
                                      name={question.contextualQuestion.name}
                                      type="radio"
                                      value={opt}
                                      onChange={handleParameterChange}
                                      checked={selectedParameters[question.contextualQuestion.name] === opt}
                                      className={`h-4 w-4 text-[${COLORS.primaryBlue}]`}
                                    />
                                    <label htmlFor={`${question.contextualQuestion.name}_${sanitizeForId(opt)}`} className={`ml-3 text-sm text-[${COLORS.darkText}]/80`}>
                                      {opt}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </form>
      </section>
    </div>
  );
}

function LoginPage({ onLoginSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const scriptUrl = 'https://script.google.com/macros/s/AKfycbx6Fcsw30zOZYWAetgoh4wfpnvNB1u3WZAlZiZ3RV0XPjm62GN_m6BSkrEoPn9Q4hVOIA/exec';
    const callbackName = 'handleAuthResponse';
    const url = `${scriptUrl}?password=${encodeURIComponent(password)}&callback=${callbackName}`;

    window[callbackName] = (response) => {
      setLoading(false);
      document.body.removeChild(scriptTag);
      delete window[callbackName];

      if (response.status === 'success') {
        sessionStorage.setItem('isAuthenticated', 'true');
        onLoginSuccess();
      } else {
        setError('Mot de passe incorrect.');
      }
    };

    const scriptTag = document.createElement('script');
    scriptTag.src = url;
    scriptTag.onerror = () => {
      setError('Erreur de communication avec le serveur.');
      setLoading(false);
      document.body.removeChild(scriptTag);
      delete window[callbackName];
    };
    document.body.appendChild(scriptTag);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="p-8 bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <OskeyLogo className="h-16 w-16" />
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Accès à l'outil</h2>
        <p className="text-center text-gray-500 mb-6">Veuillez entrer le mot de passe.</p>
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${error ? 'border-red-500 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-300'}`}
            />
          </div>
          {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}
          <button type="submit" disabled={loading} className={`w-full py-2 px-4 text-white font-semibold rounded-lg transition-all duration-300 bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}] hover:shadow-lg disabled:opacity-50`}>
            {loading ? 'Vérification...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}

// MAIN COMPONENT
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(sessionStorage.getItem('isAuthenticated') === 'true');
  const [page, setPage] = useState('property');
  const [propertyInfo, setPropertyInfo] = useState({
    name: '',
    address: '',
    zip: '',
    city: '',
    nom_gestionnaire: '',
    nom_proprietaire: '',
  });
  const [portalUsers, setPortalUsers] = useState([]);
  const [productConfig, setProductConfig] = useState({
    porteRue: { product: '', installation: '', finishingPlate: 'Aucune', keepVigik: false },
    porteHall: { product: '', installation: '', finishingPlate: 'Aucune', keepVigik: false },
  });
  const [residentsData, setResidentsData] = useState([]);
  const [providers, setProviders] = useState([]);
  const [fileName, setFileName] = useState('Aucun fichier sélectionné');
  const [isFileLoaded, setIsFileLoaded] = useState(false);
  const [csvError, setCsvError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedParameters, setSelectedParameters] = useState(() => {
    const defaults = {};
    Object.values(parametersConfig).forEach((config) => {
      config.questions.forEach((q) => {
        if (isToggleQuestion(q)) defaults[q.name] = q.options[1];
        else if (q.type === 'slider') defaults[q.name] = q.options[0];
        else if (q.type === 'pills' || q.type === 'checkbox') defaults[q.name] = [];
        if (q.contextualQuestion) {
          if (q.contextualQuestion.type === 'pills') defaults[q.contextualQuestion.name] = [];
          else if (q.contextualQuestion.type === 'radio') defaults[q.contextualQuestion.name] = q.contextualQuestion.options[0];
        }
      });
    });
    return defaults;
  });

  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });

  const isPropertyInfoValid = propertyInfo.name && propertyInfo.address && propertyInfo.zip && propertyInfo.city && propertyInfo.nom_gestionnaire && propertyInfo.nom_proprietaire;
  const isProductConfigValid = productConfig.porteRue.product && productConfig.porteRue.installation && productConfig.porteHall.product && productConfig.porteHall.installation;

  useEffect(() => {
    const errors = [];
    if (!isPropertyInfoValid) errors.push('Les informations sur la propriété sont incomplètes.');
    if (!isProductConfigValid) errors.push('La configuration des produits est incomplète.');
    if (!isFileLoaded) errors.push("Le fichier des résidents n'a pas été importé.");

    const hasIntercom = productConfig.porteRue.product === 'Intercom+' || productConfig.porteHall.product === 'Intercom+';

    Object.entries(parametersConfig).forEach(([category, config]) => {
      if (category === 'Intercom+' && !hasIntercom) return;
      if (config === parametersConfig['Paramétrage code PIN'] && !(selectedParameters.access_methods || []).includes('Code PIN')) return;

      config.questions.forEach((q) => {
        const value = selectedParameters[q.name];
        if ((q.type === 'checkbox' || q.type === 'pills') && (!value || value.length === 0)) {
          errors.push(`Le paramètre "${q.label}" doit avoir au moins une sélection.`);
        } else if (!isToggleQuestion(q) && q.type !== 'pills' && q.type !== 'checkbox' && !value) {
          errors.push(`Le paramètre "${q.label}" n'est pas renseigné.`);
        }
        if (q.contextualQuestion && value === q.options[0]) {
          const conValue = selectedParameters[q.contextualQuestion.name];
          if ((q.contextualQuestion.type === 'pills' && (!conValue || conValue.length === 0)) || (!q.contextualQuestion.type && !conValue)) {
            errors.push(`Le paramètre "${q.contextualQuestion.label}" n'est pas renseigné.`);
          }
        }
      });
    });
    setValidationErrors(errors);
  }, [isPropertyInfoValid, isProductConfigValid, isFileLoaded, selectedParameters, productConfig]);

  useEffect(() => {
    const mainAccess = selectedParameters['access_methods'] || [];
    const invitationMethodsQuestion = parametersConfig["Partage d'accès"].questions.find((q) => q.name === 'allow_invitations').contextualQuestion;

    setSelectedParameters((prev) => {
      const oldInvitationMethods = prev[invitationMethodsQuestion.name] || [];
      let newInvitationMethods = oldInvitationMethods.filter((m) => mainAccess.includes(m));
      mainAccess.forEach((m) => {
        if (!newInvitationMethods.includes(m) && invitationMethodsQuestion.options.includes(m)) newInvitationMethods.push(m);
      });
      return { ...prev, [invitationMethodsQuestion.name]: newInvitationMethods };
    });
  }, [selectedParameters['access_methods']]);

  const handleConfirmAndSubmit = async () => {
    setIsLoading(true);
    setStatusMessage({ text: '', type: '' });
    const finalResidentsData = isFileLoaded ? residentsData : [];
    const payload = { propertyInfo, productConfig, portalUsers, residents: finalResidentsData, providers, parameters: selectedParameters, submittedAt: new Date().toISOString() };

    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbx6Fcsw30zOZYWAetgoh4wfpnvNB1u3WZAlZiZ3RV0XPjm62GN_m6BSkrEoPn9Q4hVOIA/exec';

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify(payload),
      });
      setStatusMessage({ text: 'Configuration envoyée avec succès !', type: 'success' });
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setStatusMessage({ text: "Une erreur est survenue lors de l'envoi.", type: 'error' });
    } finally {
      setIsLoading(false);
      //setIsModalOpen(false);
    }
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // We reset the status message after closing so that the summary is displayed next time
    if (statusMessage.text) {
      setStatusMessage({ text: '', type: '' });
    }
  };
  const getSummary = () => {
    const summary = {};

    const propertyInfoLabels = {
      nom_prenom_soumissionnaire: 'Votre Nom & Prénom',
      email_soumissionnaire: 'Votre Email',
      nom_gestionnaire: 'Nom du gestionnaire',
      nom_proprietaire: 'Nom du propriétaire',
      name: 'Nom de la propriété',
      address: 'Adresse',
      zip: 'Code Postal',
      city: 'Commune',
    };
    const propertyDetails = {};
    for (const key in propertyInfoLabels) {
      if (propertyInfo[key]) {
        propertyDetails[propertyInfoLabels[key]] = propertyInfo[key];
      }
    }
    if (Object.keys(propertyDetails).length > 0) {
      summary['Informations Générales'] = propertyDetails;
    }

    if (productConfig && productConfig.porteRue.product) {
      summary['Configuration Produit'] = {
        'Porte Rue': `${productConfig.porteRue.product} (${productConfig.porteRue.installation}, Tôle: ${productConfig.porteRue.finishingPlate}, Vigik: ${productConfig.porteRue.keepVigik ? 'Oui' : 'Non'})`,
        'Porte Hall': `${productConfig.porteHall.product} (${productConfig.porteHall.installation}, Tôle: ${productConfig.porteHall.finishingPlate}, Vigik: ${productConfig.porteHall.keepVigik ? 'Oui' : 'Non'})`,
      };
    }

    if (portalUsers.length > 0) {
      summary['Utilisateurs Portail'] = {};
      portalUsers.forEach((p, index) => {
        summary['Utilisateurs Portail'][`Utilisateur ${index + 1}`] = `${p.nom_prenom}, ${p.email}, ${p.telephone}`;
      });
    }

    if (providers.length > 0) {
      summary['Prestataires'] = {};
      providers.forEach((p, index) => {
        summary['Prestataires'][`Prestataire ${index + 1}`] = `${p.societe}, ${p.adresse}, ${p.code_postal} ${p.commune}`;
      });
    }

    for (const [category, config] of Object.entries(parametersConfig)) {
      if (category === 'Paramétrage code PIN' && !(selectedParameters.access_methods || []).includes('Code PIN')) continue;

      const categorySummary = {};
      let hasValues = false;

      for (const question of config.questions) {
        const value = selectedParameters[question.name];
        if (value && (!Array.isArray(value) || value.length > 0)) {
          categorySummary[question.label] = Array.isArray(value) ? value.join(', ') : value;
          hasValues = true;
        }
        if (question.contextualQuestion && value === question.options[0]) {
          const conValue = selectedParameters[question.contextualQuestion.name];
          if (conValue && (!Array.isArray(conValue) || conValue.length > 0)) {
            categorySummary[question.contextualQuestion.label] = Array.isArray(conValue) ? conValue.join(', ') : conValue;
            hasValues = true;
          }
        }
      }

      if (hasValues) {
        summary[category] = categorySummary;
      }
    }
    return summary;
  };

  const getStatusColor = () => {
    if (statusMessage.type === 'error') return `text-[${COLORS.error}]`;
    if (statusMessage.type === 'success') return `text-[${COLORS.success}]`;
    return `text-[${COLORS.darkText}]/80`;
  };

  const navigateTo = (nextPage) => {
    if (page === 'property' && !isPropertyInfoValid) return;
    if (page === 'product_config' && !isProductConfigValid) return;
    setPage(nextPage);
  };

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <>
      <style>{`
                .range-thumb-gradient::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    background-image: linear-gradient(to right, ${COLORS.gradientFrom}, ${COLORS.gradientTo});
                    cursor: pointer;
                    border-radius: 50%;
                    margin-top: -7px;
                }
                .range-thumb-gradient::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    background-image: linear-gradient(to right, ${COLORS.gradientFrom}, ${COLORS.gradientTo});
                    cursor: pointer;
                    border-radius: 50%;
                }
            `}</style>
      <ConfirmationModal isOpen={isModalOpen} onClose={handleCloseModal} onConfirm={handleConfirmAndSubmit} summary={getSummary()} isLoading={isLoading} statusMessage={statusMessage} />
      <div className="font-['Nunito_Sans']" style={{ backgroundColor: COLORS.lightBackground, color: COLORS.darkText }}>
        <div className="container mx-auto p-4 md:p-8 max-w-5xl">
          <header className="mb-8">
            <div className="flex items-center">
              <OskeyLogo className="h-12 w-12 mr-4" />
              <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-violet-500" style={{ '--tw-gradient-from': COLORS.gradientFrom, '--tw-gradient-to': COLORS.gradientTo }}>
                Configurer ma solution Oskey
              </h1>
            </div>
            <p className="mt-2 text-lg text-gray-600">Cet outil vous permet de nous transmettre facilement la liste de vos résidents et de choisir les paramètres de votre solution.</p>
          </header>

          <main>
            {page === 'property' && <PropertyPage propertyInfo={propertyInfo} setPropertyInfo={setPropertyInfo} portalUsers={portalUsers} setPortalUsers={setPortalUsers} />}
            {page === 'product_config' && <ProductConfigPage productConfig={productConfig} setProductConfig={setProductConfig} />}
            {page === 'parameters' && <ParametersPage selectedParameters={selectedParameters} setSelectedParameters={setSelectedParameters} productConfig={productConfig} />}
            {page === 'residents' && (
              <ResidentsPage
                residentsData={residentsData}
                setResidentsData={setResidentsData}
                fileName={fileName}
                setFileName={setFileName}
                isFileLoaded={isFileLoaded}
                setIsFileLoaded={setIsFileLoaded}
                csvError={csvError}
                setCsvError={setCsvError}
                providers={providers}
                setProviders={setProviders}
              />
            )}
          </main>

          <footer className="mt-10 pt-6 border-t flex justify-between items-center" style={{ borderColor: COLORS.borderColor }}>
            <div>
              {page !== 'property' && (
                <button
                  onClick={() => navigateTo(page === 'product_config' ? 'property' : page === 'parameters' ? 'product_config' : 'parameters')}
                  className="bg-gray-200 font-semibold py-3 px-8 rounded-xl hover:bg-gray-300 transition-all text-lg shadow-md"
                  style={{ color: COLORS.darkText }}
                >
                  Précédent
                </button>
              )}
            </div>
            <div>
              {page !== 'residents' ? (
                <button
                  onClick={() => navigateTo(page === 'property' ? 'product_config' : page === 'product_config' ? 'parameters' : 'residents')}
                  disabled={(page === 'property' && !isPropertyInfoValid) || (page === 'product_config' && !isProductConfigValid)}
                  className={`text-white font-bold py-3 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 bg-gradient-to-r from-[${COLORS.gradientFrom}] to-[${COLORS.gradientTo}]`}
                >
                  Suivant
                </button>
              ) : (
                <div className="relative inline-block group">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    disabled={validationErrors.length > 0 || isLoading}
                    className={`text-white font-bold py-3 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 bg-gradient-to-r from-[${COLORS.success}] to-green-600`}
                  >
                    Envoyer à Oskey
                  </button>
                  {validationErrors.length > 0 && !isLoading && (
                    <div className="absolute bottom-full right-0 z-10 -translate-x-0 mb-2 w-max max-w-sm p-3 text-sm font-normal text-white bg-gray-900 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <p className="font-bold mb-2 text-left">Actions requises :</p>
                      <ul className="list-disc list-inside text-left">
                        {validationErrors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                      <div className="absolute top-full right-4 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}
